// Meu Perfil (PO1-1918) — visualizar dados, editar nome/sobrenome, trocar
// foto, alterar senha. Layout: container "hero" envolvendo subcards (mesmo
// padrão do Monitor de integrações do Autopilot — InsightsCard).

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";

import api from "../../../services/api";
// PO1-2722: o redimensionamento no canvas virou util compartilhado com o logo
// da transportadora (mesmo crop central quadrado, mesmo JPEG 0.85 aqui).
import { resizeImageToDataUrl } from "../../../utils/resizeImage";
import { useAuth } from "../../../hooks/auth";
import { rotaInicial } from "../../../routes/products";
import { useToast } from "../../../hooks/toast";
import UserAvatar from "../../../components/UserAvatar/UserAvatar";
import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotSecondaryButtonStyle,
  autopilotTitleStyle,
} from "../../../components/autopilot/styles";

const PASSWORD_HINT =
  "Mínimo 8 caracteres com 3 dos seguintes: minúscula, maiúscula, número, símbolo.";

const PASSWORD_ERROR_MAP = {
  senha_obrigatoria: "A senha é obrigatória.",
  min_8_chars: "A senha deve ter pelo menos 8 caracteres.",
  min_3_classes:
    "A senha deve conter ao menos 3 de: minúscula, maiúscula, número, símbolo.",
  senha_igual_a_atual: "A nova senha não pode ser igual à atual.",
  senha_atual_incorreta: "Senha atual incorreta.",
  usuario_invalido: "Usuário inválido ou inativo.",
};

const AVATAR_MAX_SIZE_PX = 256;
const AVATAR_ACCEPTED_MIME = ["image/png", "image/jpeg", "image/webp"];

function translatePasswordError(err) {
  const data = err && err.response && err.response.data;
  if (!data) return "Falha ao alterar senha.";
  if (data.motivo && PASSWORD_ERROR_MAP[data.motivo]) {
    return PASSWORD_ERROR_MAP[data.motivo];
  }
  if (data.error && typeof data.error === "string") {
    const motivo = data.error.split(":").pop();
    if (PASSWORD_ERROR_MAP[motivo]) return PASSWORD_ERROR_MAP[motivo];
  }
  return data.error || "Falha ao alterar senha.";
}

const dateFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const dateTimeFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function formatDate(value) {
  if (!value) return "—";
  try {
    return dateFormatter.format(new Date(value));
  } catch (_) {
    return "—";
  }
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return dateTimeFormatter.format(new Date(value));
  } catch (_) {
    return "—";
  }
}

// ─── Linha de resumo (label + valor) usada no SubCard "Resumo da conta" ───

function SummaryRow({ label, value, last }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: "0.75rem",
        padding: "0.7rem 0",
        borderBottom: last ? "none" : `1px solid ${AUTOPILOT_COLORS.divider}`,
      }}
    >
      <span
        style={{
          color: AUTOPILOT_COLORS.muted,
          fontSize: "0.84rem",
          fontWeight: 500,
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span
        style={{
          color: AUTOPILOT_COLORS.text,
          fontSize: "0.86rem",
          fontWeight: 600,
          textAlign: "right",
          minWidth: 0,
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Subcard local (mesmo padrão do SubCard do InsightsCard) ──────────────

function SubCard({ title, children, style }) {
  return (
    <div
      style={{
        border: `1px solid ${AUTOPILOT_COLORS.border}`,
        borderRadius: 14,
        padding: "14px 18px",
        marginBottom: 14,
        background: AUTOPILOT_COLORS.insetSurface,
        ...style,
      }}
    >
      {title && (
        <>
          <p
            style={{
              fontSize: "0.78rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: AUTOPILOT_COLORS.accent,
              margin: "0 0 0.5rem 0",
            }}
          >
            {title}
          </p>
          <div
            style={{
              borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
              marginBottom: "0.85rem",
            }}
          />
        </>
      )}
      {children}
    </div>
  );
}

// Inputs editáveis ganham mais destaque: texto branco/title + peso 600 +
// borda mais visível. Diferencia visualmente dos ReadOnlyField (que usam
// textSoft + cadeado).
const editableInputStyle = {
  color: AUTOPILOT_COLORS.title,
  fontWeight: 600,
  borderColor: AUTOPILOT_COLORS.border,
};

const ghostButtonStyle = {
  background: "transparent",
  borderColor: AUTOPILOT_COLORS.borderFaint,
  color: AUTOPILOT_COLORS.text,
  fontWeight: 600,
};

const linkButtonStyle = {
  background: "transparent",
  border: "none",
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.78rem",
  textDecoration: "underline",
  padding: "4px 6px",
};

// Pseudo-input read-only — espelha o visual de <Input disabled> do reactstrap
// mas com cadeado discreto à direita pra reforçar que o campo é bloqueado.
function ReadOnlyField({ label, value }) {
  return (
    <FormGroup>
      <Label>{label}</Label>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.5rem 0.75rem",
          minHeight: "calc(2.25rem + 2px)",
          borderRadius: "0.4285rem",
          border: `1px solid ${AUTOPILOT_COLORS.borderSoft}`,
          background: "transparent",
          color: AUTOPILOT_COLORS.textSoft,
          fontSize: "0.875rem",
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={value || ""}
        >
          {value || "—"}
        </span>
        <i
          className="tim-icons icon-lock-circle"
          style={{
            color: AUTOPILOT_COLORS.textSoft,
            fontSize: "1rem",
            flexShrink: 0,
          }}
          title="Campo não editável"
        />
      </div>
    </FormGroup>
  );
}

// Botão "fechar" no topo direito do hero — volta pro Autopilot.
// Mesmo padrão da AuditoriaCTe / DetalheEmissaoModal / TabelaFreteModal:
// "×" grande sem borda, cor #d7e7ff.
const closeButtonStyle = {
  position: "absolute",
  top: 14,
  right: 18,
  zIndex: 1,
  border: 0,
  background: "transparent",
  color: "#d7e7ff",
  fontSize: "2rem",
  lineHeight: 1,
  padding: 0,
  cursor: "pointer",
  opacity: 0.95,
  textShadow: "none",
};

const CloseButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Fechar"
    title="Fechar"
    style={closeButtonStyle}
  >
    ×
  </button>
);

const Perfil = () => {
  const { addToast } = useToast();
  const { updateUser, user } = useAuth();
  const history = useHistory();
  const fileInputRef = useRef(null);
  // PO1-2287: fecha na aterrissagem do setor (transportadora não tem Autopilot).
  const handleClose = () => history.push(rotaInicial(user));

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [profileError, setProfileError] = useState(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);

  const loadMe = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/users/me");
      setMe(response.data);
      setName(response.data.name || "");
      setSurname(response.data.surname || "");
    } catch (err) {
      setProfileError("Não foi possível carregar seu perfil.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const displayUser = me;
  const hasCustomAvatar = Boolean(displayUser && displayUser.avatarUrl);
  const fullName =
    (me && (me.name || me.surname))
      ? `${me.name || ""} ${me.surname || ""}`.trim()
      : me && me.userName;

  const handlePickFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // Auto-save: PUT só com avatarUrl. Dados pessoais (nome/sobrenome) seguem
  // exigindo o botão "Salvar alterações" — fluxos independentes.
  const persistAvatar = async (nextAvatarUrl, successTitle) => {
    setSavingAvatar(true);
    setProfileError(null);
    try {
      const response = await api.put("/users/me", { avatarUrl: nextAvatarUrl });
      setMe(response.data);
      updateUser({ avatarUrl: response.data.avatarUrl });
      addToast({
        place: "tr",
        type: "success",
        icon: "tim-icons icon-check-2",
        title: successTitle,
      });
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        "Falha ao atualizar a foto.";
      setProfileError(msg);
    } finally {
      setSavingAvatar(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;

    if (!AVATAR_ACCEPTED_MIME.includes(file.type)) {
      setProfileError("Formato não suportado. Use PNG, JPG ou WebP.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setProfileError("Imagem muito grande. Use até 8MB.");
      return;
    }

    try {
      const dataUrl = await resizeImageToDataUrl(file, AVATAR_MAX_SIZE_PX);
      await persistAvatar(dataUrl, "Foto atualizada");
    } catch (err) {
      setProfileError(err.message || "Falha ao processar imagem.");
    }
  };

  const handleRemoveAvatar = async () => {
    await persistAvatar("", "Foto removida");
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileError(null);
    try {
      const response = await api.put("/users/me", {
        name: name.trim(),
        surname: surname.trim(),
      });
      setMe(response.data);

      updateUser({
        name: response.data.name,
        surname: response.data.surname,
      });

      addToast({
        place: "tr",
        type: "success",
        icon: "tim-icons icon-check-2",
        title: "Perfil atualizado",
      });
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        "Falha ao atualizar perfil.";
      setProfileError(msg);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    if (newPassword !== confirmPassword) {
      setPasswordError("A confirmação não confere com a nova senha.");
      return;
    }

    setSavingPassword(true);
    try {
      await api.post("/users/me/change-password", {
        currentPassword,
        newPassword,
      });
      addToast({
        place: "tr",
        type: "success",
        icon: "tim-icons icon-check-2",
        title: "Senha alterada",
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPasswordError(translatePasswordError(err));
    } finally {
      setSavingPassword(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="content">
        <Container fluid>
          <Card style={autopilotCardStyle}>
            <CardBody style={autopilotBodyStyle}>
              <p style={{ color: AUTOPILOT_COLORS.muted, margin: 0 }}>
                Carregando perfil...
              </p>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="content">
      <Container fluid>
        <Card
          style={{
            ...autopilotCardStyle,
            background: "var(--everlog-card-hero-bg)",
            position: "relative",
          }}
        >
          <CloseButton onClick={handleClose} />
          <CardHeader style={autopilotHeaderStyle}>
            <div style={autopilotEyebrowStyle}>Minha Conta</div>
            <div style={{ ...autopilotTitleStyle, marginBottom: "0.25rem" }}>
              Meu perfil
            </div>
            <p style={{ ...autopilotDescriptionStyle, marginBottom: 0 }}>
              Edite seus dados de identificação, atualize sua foto ou troque a
              senha de acesso ao Hub.
            </p>
          </CardHeader>

          <CardBody style={autopilotBodyStyle}>
            <Row>
              {/* ─── Coluna identidade ─── */}
              <Col
                lg="4"
                md="5"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <SubCard style={{ marginBottom: 14 }}>
                  <div style={{ textAlign: "center" }}>
                    <UserAvatar user={displayUser} size={128} />
                    <div
                      style={{
                        ...autopilotTitleStyle,
                        marginTop: "1rem",
                        marginBottom: "0.15rem",
                      }}
                    >
                      {fullName || "—"}
                    </div>
                    <div
                      style={{
                        color: AUTOPILOT_COLORS.muted,
                        fontSize: "0.84rem",
                      }}
                    >
                      {me && me.email}
                    </div>
                    {me && me.setor && (
                      <div
                        style={{
                          display: "inline-flex",
                          marginTop: "0.75rem",
                          padding: "0.2rem 0.7rem",
                          borderRadius: 999,
                          background: AUTOPILOT_COLORS.surfacePill,
                          color: AUTOPILOT_COLORS.textSoft,
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        {me.setor}
                      </div>
                    )}

                    <div
                      style={{
                        marginTop: "1.25rem",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "0.4rem",
                      }}
                    >
                      <Button
                        type="button"
                        size="sm"
                        style={ghostButtonStyle}
                        onClick={handlePickFile}
                        disabled={savingAvatar}
                      >
                        {savingAvatar
                          ? "Salvando..."
                          : hasCustomAvatar
                          ? "Trocar foto"
                          : "Adicionar foto"}
                      </Button>
                      {hasCustomAvatar && (
                        <button
                          type="button"
                          style={linkButtonStyle}
                          onClick={handleRemoveAvatar}
                          disabled={savingAvatar}
                        >
                          Remover foto
                        </button>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={AVATAR_ACCEPTED_MIME.join(",")}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                      />
                    </div>
                  </div>
                </SubCard>

                <SubCard
                  title="Resumo da conta"
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    marginBottom: 0,
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-around",
                    }}
                  >
                    <SummaryRow
                      label="Conta criada em"
                      value={formatDate(me && me.creationTime)}
                    />
                    <SummaryRow
                      label="Último acesso Hub"
                      value={formatDateTime(me && me.lastLoginHubAt)}
                    />
                    <SummaryRow
                      label="Último acesso Flow"
                      value={formatDateTime(me && me.lastLoginFlowAt)}
                      last
                    />
                  </div>
                </SubCard>
              </Col>

              {/* ─── Coluna formulários ─── */}
              <Col lg="8" md="7">
                <SubCard title="Dados pessoais">
                  <Form onSubmit={handleSaveProfile}>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label>Nome</Label>
                          <Input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={64}
                            required
                            style={editableInputStyle}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>Sobrenome</Label>
                          <Input
                            type="text"
                            value={surname}
                            onChange={(e) => setSurname(e.target.value)}
                            maxLength={64}
                            style={editableInputStyle}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="6">
                        <ReadOnlyField
                          label="E-mail"
                          value={me && me.email}
                        />
                      </Col>
                      <Col md="6">
                        <ReadOnlyField
                          label="Nome de usuário"
                          value={me && me.userName}
                        />
                      </Col>
                    </Row>

                    {profileError && (
                      <Alert color="danger" style={{ marginTop: "0.25rem" }}>
                        {profileError}
                      </Alert>
                    )}

                    <div
                      style={{
                        marginTop: "0.5rem",
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        type="submit"
                        style={autopilotSecondaryButtonStyle}
                        disabled={savingProfile}
                      >
                        {savingProfile ? "Salvando..." : "Salvar alterações"}
                      </Button>
                    </div>
                  </Form>
                </SubCard>

                <SubCard title="Segurança · Alterar senha" style={{ marginBottom: 0 }}>
                  <p
                    style={{
                      ...autopilotDescriptionStyle,
                      marginBottom: "0.9rem",
                    }}
                  >
                    Sua senha atual continua valendo até você confirmar a troca.
                    Sessões em outros dispositivos serão encerradas.
                  </p>
                  <Form onSubmit={handleChangePassword}>
                    <Row>
                      <Col md="12">
                        <FormGroup>
                          <Label>Senha atual</Label>
                          <Input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label>Nova senha</Label>
                          <Input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            autoComplete="new-password"
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>Confirmar nova senha</Label>
                          <Input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            autoComplete="new-password"
                            required
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <p
                      style={{
                        color: AUTOPILOT_COLORS.muted,
                        fontSize: "0.78rem",
                        marginBottom: "0.5rem",
                      }}
                    >
                      {PASSWORD_HINT}
                    </p>

                    {passwordError && (
                      <Alert color="danger" style={{ marginTop: "0.25rem" }}>
                        {passwordError}
                      </Alert>
                    )}

                    <div
                      style={{
                        marginTop: "0.5rem",
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        type="submit"
                        style={autopilotSecondaryButtonStyle}
                        disabled={
                          savingPassword ||
                          !currentPassword ||
                          !newPassword ||
                          !confirmPassword
                        }
                      >
                        {savingPassword ? "Alterando..." : "Alterar senha"}
                      </Button>
                    </div>
                  </Form>
                </SubCard>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default Perfil;
